import React, { useState, useEffect } from "react"
import styled from "styled-components"

const ContactContainer = styled.section`
  id: contact;
  padding: ${({ theme }) => `${theme.spacing.content.section} ${theme.spacing.content.base}`};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.overlay.dark}D9 0%, ${({ theme }) => theme.colors.overlay.medium}D9 100%),
    url("/images/contact/contact-section-background.jpg");
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  color: ${({ theme }) => theme.colors.text.inverse};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing["3xl"]} ${theme.spacing.content.narrow}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing["2xl"]} ${theme.spacing.content.narrow}`};
  }
`

const ContactContent = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  text-align: center;
`

const ContactTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize["4xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.inverse};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize["3xl"]};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  }
`

const ContactSubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.inverse};
  opacity: 0.9;
  margin: 0 0 ${({ theme }) => theme.spacing["3xl"]} 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`

const ContactLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${({ theme }) => theme.spacing["3xl"]};
  align-items: start;
  margin-bottom: ${({ theme }) => theme.spacing["3xl"]};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing["2xl"]};
  }
`

const ConsultationInfo = styled.div`
  background: ${({ theme }) =>
    `linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.light} 100%)`};
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: ${({ theme }) => theme.spacing["3xl"]};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadowsEnhanced.xl};
  text-align: left;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    text-align: center;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing["2xl"]};
  }
`

const ConsultationTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`

const ConsultationDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  opacity: 0.95;
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
`

const Benefit = styled.li`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  padding-left: ${({ theme }) => theme.spacing.lg};
  position: relative;
  line-height: ${({ theme }) => theme.typography.lineHeight.base};

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.accent.copper};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`

const PreparationSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  margin-top: ${({ theme }) => theme.spacing.xl};
  backdrop-filter: blur(10px);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`

const PreparationTitle = styled.h5`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  color: ${({ theme }) => theme.colors.white};
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`

const PreparationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    text-align: center;
  }
`

const PreparationItem = styled.li`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  padding-left: ${({ theme }) => theme.spacing.lg};
  position: relative;

  &:before {
    content: "→";
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.accent.copper};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding-left: ${({ theme }) => theme.spacing.base};
    text-align: center;

    &:before {
      display: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }
`

const NoCommitmentNote = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-style: italic;
  text-align: center;
  margin: 0;
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`

const SchedulingContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 4px 12px rgba(184, 115, 51, 0.15);
  border: 2px solid ${({ theme }) => theme.colors.accent.copper};
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
    min-height: 300px;
  }
`

const SchedulingTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`

const SchedulingSubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.accent.copper};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`

const SchedulingDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  max-width: 300px;
`

const TrustSignals = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.muted};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.xs};
  }
`

const TrustSignal = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:before {
    content: "✓";
    color: ${({ theme }) => theme.colors.accent.copper};
    font-weight: bold;
  }
`

const CTAButton = styled.button`
  background: ${({ theme }) => theme.colors.accent.copper};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => `${theme.spacing.base} ${theme.spacing.xl}`};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitionsEnhanced.base};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 280px;

  &:hover {
    background: ${({ theme }) => theme.colors.accent.copper}E6;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(184, 115, 51, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
    min-width: 240px;
  }
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: transparent;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
  box-sizing: border-box;
`

const OverlayContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadowsEnhanced["lg"]};
  width: 100%;
  max-width: 1000px;
  height: 90vh;
  overflow: hidden;
  position: relative;
  margin: auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 95vw;
    height: 95vh;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 98vw;
    height: 98vh;
  }
`

const OverlayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.white};
`

const OverlayTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.typography.fontSize["2xl"]};
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.base};
  transition: all ${({ theme }) => theme.transitionsEnhanced.base};

  &:hover {
    background: ${({ theme }) => theme.colors.background.muted};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

const CalendlyWidget = styled.div`
  width: 100%;
  flex: 1;
  border-radius: 0 0 ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.white};
  
  .calendly-inline-widget {
    width: 100% !important;
    height: 100% !important;
    min-height: 700px !important;
  }
`

// Calendly configuration
const CALENDLY_URL = "https://calendly.com/d/csxp-hzj-jc2"
const CALENDLY_SCRIPT_URL = "https://assets.calendly.com/assets/external/widget.js"

const ContactSection = () => {
  const [showOverlay, setShowOverlay] = useState(false)

  const handleScheduleClick = () => {
    setShowOverlay(true)
  }

  const handleCloseOverlay = () => {
    setShowOverlay(false)
  }

  // Handle escape key to close overlay and load Calendly script
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowOverlay(false)
      }
    }

    if (showOverlay) {
      document.addEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'hidden'
      
      // Load Calendly script if not already loaded
      if (!document.querySelector(`script[src="${CALENDLY_SCRIPT_URL}"]`)) {
        const script = document.createElement('script')
        script.src = CALENDLY_SCRIPT_URL
        script.async = true
        document.head.appendChild(script)
      }
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
      document.body.style.overflow = 'unset'
    }
  }, [showOverlay])

  return (
    <>
      <ContactContainer id="contact">
        <ContactContent>
          <ContactTitle>Schedule Your Zoom Consultation</ContactTitle>
          <ContactSubtitle>
            Begin with a free 30-minute strategic Zoom call to explore current challenges, assess mutual fit, and discuss
            transformation pathways without commitment.
          </ContactSubtitle>

          <ContactLayout>
            <ConsultationInfo>
              <ConsultationTitle>Free 30-Minute Zoom Strategy Session</ConsultationTitle>
              <ConsultationDescription>
                Explore current IT/OT integration challenges, assess strategic technology leadership needs, and evaluate
                mutual fit for long-term partnership collaboration. Our consultation process is designed to provide
                immediate value while determining if there's a strategic alignment for deeper engagement.
              </ConsultationDescription>

              <BenefitsList>
                <Benefit>Analyze current operational challenges and their impact on efficiency</Benefit>
                <Benefit>Assess strategic technology leadership gaps and opportunities</Benefit>
                <Benefit>Evaluate cultural fit and partnership potential for long-term success</Benefit>
                <Benefit>Discuss transformation timeline, priorities, and investment expectations</Benefit>
                <Benefit>Receive strategic clarity with competitive positioning insights</Benefit>
                <Benefit>Understand proven methodologies for manufacturing excellence</Benefit>
              </BenefitsList>

              <PreparationSection>
                <PreparationTitle>Preparation for Maximum Value</PreparationTitle>
                <PreparationList>
                  <PreparationItem>Current operational challenges and urgency level assessment</PreparationItem>
                  <PreparationItem>Technology infrastructure overview and critical pain points</PreparationItem>
                  <PreparationItem>Strategic objectives for competitive positioning and growth</PreparationItem>
                  <PreparationItem>Timeline expectations for transformation implementation phases</PreparationItem>
                  <PreparationItem>Budget parameters and investment authorization levels</PreparationItem>
                </PreparationList>
              </PreparationSection>

              <NoCommitmentNote>No pressure. No commitment. Strategic clarity guaranteed.</NoCommitmentNote>
            </ConsultationInfo>

            <SchedulingContainer>
              <SchedulingTitle>Ready to Get Started?</SchedulingTitle>
              <SchedulingSubtitle>FREE 30-minute consultation</SchedulingSubtitle>
              <SchedulingDescription>
                A brief questionnaire helps us prepare for a productive meeting focused on your specific challenges and objectives.
              </SchedulingDescription>

              <TrustSignals>
                <TrustSignal>30-minute consultation</TrustSignal>
                <TrustSignal>No commitment</TrustSignal>
              </TrustSignals>

              <CTAButton onClick={handleScheduleClick}>
                Schedule Zoom Consultation Now
              </CTAButton>
            </SchedulingContainer>
          </ContactLayout>
        </ContactContent>
      </ContactContainer>

      {showOverlay && (
        <Overlay onClick={handleCloseOverlay}>
          <OverlayContent onClick={(e) => e.stopPropagation()}>
            <OverlayHeader>
              <OverlayTitle>Schedule Your Zoom Consultation</OverlayTitle>
              <CloseButton onClick={handleCloseOverlay} aria-label="Close">
                ×
              </CloseButton>
            </OverlayHeader>
            <CalendlyWidget>
              <div 
                className="calendly-inline-widget" 
                data-url={CALENDLY_URL}
                style={{
                  minWidth: '320px',
                  height: '700px',
                  width: '100%'
                }}
              />
            </CalendlyWidget>
          </OverlayContent>
        </Overlay>
      )}
    </>
  )
}

export default ContactSection